// Posible states for reducers
export enum ReducerStates {
  IDLE = "IDLE",
  PENDING = "PENDING",
  SUCCEEDED = "SUCCEEDED",
  FAILED = "FAILED",
}

export enum Temperature {
  CELSIUS = "C",
  FAHRENHEIT = "F",
}

export enum TimeFormat {
  TIME24H = "24h",
  TIME12H = "12h",
}

export const Views = {
  ROOT: "/",
  LOGIN: "/login",
  LOGIN_CONFIRMATION: "/login/confirmation",
  LOGIN_LOAD_SUBSCRIPTION: "/login/subscription",
  DASHBOARD: "/dashboard",
  SUBSCRIPTION: "/subscription",
  SUBSCRIPTION_MANAGEMENT: "/subscription/update",
  SUBSCRIPTION_CONFIRMATION: "/subscription/confirmation",
  ACCESS_EXPIRED: "/access_expired",
  BILLING_MANAGERS: "/subscription/billing_managers",
};

export enum FilterOrder {
  desc,
  asc,
}

export const EMAIL_CHAR_LIMIT = 20;

export const BASE64_STRING = ":base64:";
export const LIMIT_TO_VALIDATE_BASE64 = 350;
export const BASE64_COMMENT_CHAR_LIMIT = 2048;
export const ISO_TIME_SAVE_FORMAT = "HH:mm";

export const VERIFICATION_CODE_LENGTH = 6;

export const TEST = "care-api-test";
export const STAGING = "care-api-staging";
export const PRODUCTION = "care-api-prod";
export const DefaultRegionCode = "US";

export const SupportedLanguages = [
  { id: "en", label: "english" },
  { id: "en-GB", label: "english_GB" },
  { id: "de", label: "german" },
  { id: "es", label: "spanish" },
  { id: "fr", label: "french" },
  { id: "nl", label: "dutch" },
] as const;
/**
 * Hex opacity cheatsheet
 * Add this code at the end of hex color to add opacity 
  100% — FF
  95% — F2
  90% — E6
  85% — D9
  80% — CC
  75% — BF
  70% — B3
  65% — A6
  60% — 99
  55% — 8C
  50% — 80
  45% — 73
  40% — 66
  35% — 59
  30% — 4D
  25% — 40
  20% — 33
  15% — 26
  10% — 1A
  5% — 0D
  0% — 00
 */

export const brandPrimaryColor = "#09BE86";

// Theme for StackCare Billing App
export let appTheme = {
  typography: {
    fontFamily: ["Helvetica Neue", "sans-serif"].join(","),
    body1: {
      lineHeight: 1,
    },
    primaryFontFamily: ["Helvetica Neue", "sans-serif"].join(","),
    secondaryFontFamily: ["Helvetica Neue", "sans-serif"].join(","),
    tertiaryFontFamily: ["Helvetica Neue", "sans-serif"].join(","),
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1430,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: brandPrimaryColor,
      trans: "#7CC5AF",
      contrastText: "#fff",
      light: "#F6F6F6",
    },
    error: {
      main: "#EE3B0C",
      anomaly: "#EE3B0CBF",
      trans: "#EE3B0C40",
      light: "#F4896C",
      lightTrans: "#F4896C80",
      contrastText: "#EE3B0C",
    },
    warning: {
      main: "#EE9B0C",
      light: "#F9C97A",
      lightTrans: "#F4C26C80",
      contrastText: "#EE9B0C",
    },
    paused: {
      main: "#404040",
      light: "#808080",
      trans: "#C0C0C0",
      contrastText: "#404040",
    },
    normal: {
      main: brandPrimaryColor,
      trans: "#7CC5AF",
    },
    metrics: {
      main: "#82B508",
    },
    indicator: {
      main: "#1ADCFF",
    },
    dark: {
      main: "#000000",
      light: "#4A4A4A",
      dark: "#000",
    },
    light: {
      main: "#E8E8E8",
      light: "#FFF",
    },
    gray: {
      main: "#808080",
      light: "#FFF",
    },
    gradientNight: {
      light: "#33333380",
      dark: "#333333CC",
    },
    gradientDay: {
      light: `${brandPrimaryColor}80`,
      dark: `${brandPrimaryColor}CC`,
    },
    gradientMorning: {
      light: "#09AFBE",
      dark: "#09AFBE80",
    },
    gradientAfternoon: {
      light: "#F5A623",
      dark: "#F5A623B3",
    },
    gradientEvening: {
      light: "#106899",
      dark: "#106899BF",
    },
    activityMotion: {
      main: "#7CC5AF",
    },
    contactBlue: {
      main: "#179FEC",
    },
    noData: {
      main: "#4A4A4A",
    },
    lightGrey: {
      main: "#E9E9EA",
    },
    lightTransparent: {
      main: "#FFFFFF80",
    },
  },
  overrides: {
    // Style sheet name ⚛️
    MuiListItem: {
      root: {
        "&$selected": {
          backgroundColor: brandPrimaryColor,
        },
        "&$selected:hover": {
          backgroundColor: brandPrimaryColor,
        },
        "&:hover": {
          backgroundColor: brandPrimaryColor,
        },
      },
      button: {
        "&:hover": {
          backgroundColor: brandPrimaryColor,
        },
      },
    },
    MuiButton: {
      root: {
        transition: "color .01s",
        textTransform: "capitalize" as const,
      },
      label: {
        fontFamily: "Helvetica Neue",
        fontSize: "1rem",
      },
    },
    MuiButtonBase: {
      root: {
        borderRadius: " 5px",
      },
    },
  },
  spacing: 8,
  mixins: {
    toolbar: {
      minHeight: "100px",
    },
  },
};

export const capitalize = (s: string) => {
  if (typeof s !== "string") return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const sortAlphabetical = (a?: string | null, b?: string | null) => {
  if (a === undefined || b === undefined) return 0;
  if (a === b)
    // identical? return 0
    return 0;
  else if (a === null)
    // a is null? last
    return 1;
  else if (b === null)
    // b is null? last
    return -1;
  else {
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });

    return collator.compare(a, b);
  }
};

/* truncate number to 1 decimal  */
export function truncate(value: number) {
  const str = value.toString();
  const ans = str.match(/^-?\d+(?:\.\d{0,1})?/);
  return ans !== null && ans.length > 0 ? ans[0] : `${value}`;
}

/* Truncates the string and replaces with ... for strings longer than n */
export function ellipsis(str: string, n: number) {
  return str.length > n ? str.substr(0, n - 1) + "..." : str;
}

/* Converts a string to utf-8 and then to base64 */
export function utf8_to_b64(str: string) {
  return window.btoa(unescape(encodeURIComponent(str)));
}

export const LOG_LINES_LIMIT = 1000;
